/**
 * PNG snapshot (ImageCaptureRenderer.cs:12-20, 34-112, 199-229): clamp to SNAPSHOT.maxEdge (and the GL
 * MAX_RENDERBUFFER_SIZE), 2x supersample when the longest edge <= 4096, tiled rendering above 4096 px per edge to
 * bound GPU memory, transparent background (clear 0,0,0,0, no skybox) when includeBackground is false.
 */
import { Matrix4, RGBAFormat, SRGBColorSpace, UnsignedByteType, WebGLRenderTarget, type Camera, type Scene, type WebGLRenderer } from 'three';
import { SNAPSHOT } from '@nanome-lite/look';
import { subFrustum } from './camera';

export const SNAPSHOT_TILE_EDGE = 4096;

export interface SnapshotPlan { width: number; height: number; renderWidth: number; renderHeight: number; supersampled: boolean; tiles: { x: number; y: number; w: number; h: number }[] }

/** ClampResolution: scale proportionally so the longest edge is at most maxEdge. */
export function clampResolution(width: number, height: number, maxEdge: number): [number, number] {
  width = Math.max(1, Math.round(width)); height = Math.max(1, Math.round(height));
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return [width, height];
  const s = maxEdge / longest;
  return [Math.max(1, Math.round(width * s)), Math.max(1, Math.round(height * s))];
}

export function planSnapshot(width: number, height: number, maxRenderbufferSize: number): SnapshotPlan {
  const maxEdge = Math.min(SNAPSHOT.maxEdge, maxRenderbufferSize);
  [width, height] = clampResolution(width, height, maxEdge);
  let renderWidth = width, renderHeight = height;
  if (Math.max(width, height) <= SNAPSHOT.supersampleBelowEdge) {
    [renderWidth, renderHeight] = clampResolution(width * SNAPSHOT.supersample, height * SNAPSHOT.supersample, maxEdge);
  }
  const tileEdge = Math.min(SNAPSHOT_TILE_EDGE, maxRenderbufferSize);
  const tiles: SnapshotPlan['tiles'] = [];
  for (let y = 0; y < renderHeight; y += tileEdge) for (let x = 0; x < renderWidth; x += tileEdge) tiles.push({ x, y, w: Math.min(tileEdge, renderWidth - x), h: Math.min(tileEdge, renderHeight - y) });
  return { width, height, renderWidth, renderHeight, supersampled: renderWidth !== width || renderHeight !== height, tiles };
}

export interface SnapshotRenderContext {
  renderer: WebGLRenderer;
  scene: Scene;
  camera: Camera;
  /** full-frame projection for the output aspect */
  projection: Matrix4;
  samples: number;
  includeBackground: boolean;
  /** per-target uniform setup (resolution) */
  prepare: (w: number, h: number) => void;
  /** toggle skybox visibility */
  setSkyboxVisible: (v: boolean) => void;
}

function makeCanvas(w: number, h: number): HTMLCanvasElement | OffscreenCanvas {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
  const c = document.createElement('canvas'); c.width = w; c.height = h; return c;
}

async function toBlob(c: HTMLCanvasElement | OffscreenCanvas): Promise<Blob> {
  if ('convertToBlob' in c) return c.convertToBlob({ type: 'image/png' });
  return new Promise((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error('toBlob failed'))), 'image/png'));
}

/** Render the scene tile by tile into a canvas and encode a PNG. */
export async function renderSnapshot(ctx: SnapshotRenderContext, width: number, height: number): Promise<Blob> {
  const { renderer } = ctx;
  const gl = renderer.getContext();
  const plan = planSnapshot(width, height, gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) as number);
  const big = makeCanvas(plan.renderWidth, plan.renderHeight);
  const bctx = big.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
  const tileW = Math.max(...plan.tiles.map((t) => t.w)), tileH = Math.max(...plan.tiles.map((t) => t.h));
  const target = new WebGLRenderTarget(tileW, tileH, { format: RGBAFormat, type: UnsignedByteType, depthBuffer: true, stencilBuffer: true, samples: ctx.samples, colorSpace: SRGBColorSpace });
  const prevTarget = renderer.getRenderTarget();
  const savedProj = ctx.camera.projectionMatrix.clone();
  const proj = new Matrix4();
  const pixels = new Uint8Array(tileW * tileH * 4);
  try {
    ctx.setSkyboxVisible(ctx.includeBackground);
    for (const t of plan.tiles) {
      if (target.width !== t.w || target.height !== t.h) target.setSize(t.w, t.h);
      // tile y is measured from the top of the image; GL from the bottom
      subFrustum(ctx.projection, t.x, plan.renderHeight - t.y - t.h, t.w, t.h, plan.renderWidth, plan.renderHeight, proj);
      ctx.camera.projectionMatrix.copy(proj);
      ctx.camera.projectionMatrixInverse.copy(proj).invert();
      ctx.prepare(t.w, t.h);
      renderer.setRenderTarget(target);
      renderer.setClearColor(0x000000, 0);
      renderer.clear(true, true, true);
      renderer.render(ctx.scene, ctx.camera);
      renderer.readRenderTargetPixels(target, 0, 0, t.w, t.h, pixels);
      const img = new ImageData(t.w, t.h);
      const d = img.data;
      // flip rows; un-premultiply the blended edges over a transparent clear
      for (let row = 0; row < t.h; row++) {
        const src = (t.h - 1 - row) * t.w * 4, dst = row * t.w * 4;
        for (let i = 0; i < t.w * 4; i += 4) {
          const a = pixels[src + i + 3]!;
          if (!ctx.includeBackground && a > 0 && a < 255) {
            const f = 255 / a;
            d[dst + i] = Math.min(255, pixels[src + i]! * f); d[dst + i + 1] = Math.min(255, pixels[src + i + 1]! * f); d[dst + i + 2] = Math.min(255, pixels[src + i + 2]! * f);
          } else {
            d[dst + i] = pixels[src + i]!; d[dst + i + 1] = pixels[src + i + 1]!; d[dst + i + 2] = pixels[src + i + 2]!;
          }
          d[dst + i + 3] = ctx.includeBackground ? 255 : a;
        }
      }
      bctx.putImageData(img, t.x, t.y);
    }
  } finally {
    ctx.setSkyboxVisible(true);
    renderer.setRenderTarget(prevTarget);
    ctx.camera.projectionMatrix.copy(savedProj);
    ctx.camera.projectionMatrixInverse.copy(savedProj).invert();
    target.dispose();
  }
  if (!plan.supersampled) return toBlob(big);
  const out = makeCanvas(plan.width, plan.height);
  const octx = out.getContext('2d') as CanvasRenderingContext2D;
  octx.imageSmoothingEnabled = true;
  octx.imageSmoothingQuality = 'high';
  octx.drawImage(big as HTMLCanvasElement, 0, 0, plan.width, plan.height);
  return toBlob(out);
}
